import { call, put, takeLatest, select } from 'redux-saga/effects'
import { trackInternalEvent } from '@rio/tracking'
import { State, VirtualCoachState } from 'builder/features/virtualCoach/types'
import { UserData, selectors as userSelectors } from 'builder/modules/user'
import { ApiResponse, baseClient } from '../apiClient'
import { actions, selectors } from './virtualCoachModule'

import {
  API_ENDPOINT,
  API_ENDPOINT_HISTORY,
  CLIENT_STORAGE_KEY,
  DEFAULT_STATE,
  ERROR_MESSAGE,
} from './constants'

interface AiHttpResponse {
  response: string
  url_links: string[]
}

type ResolveResponse<T> = { success: true; body: T } | { success: false; body: string }
type AiResponse = ResolveResponse<AiHttpResponse>

function reverseByPairs(history: State) {
  const pairs: [State[number], State[number]][] = []
  for (let i = 0; i < history.length; i += 2) {
    const first = history[i]
    const second = history[i + 1]

    if (first.role === 'user' && second.role === 'assistant') {
      pairs.push([first, second])
    } else if (first.role === 'assistant' && second.role === 'user') {
      pairs.push([second, first])
    } else {
      pairs.push([first, second])
    }
  }

  const flattened = pairs.reverse().flat().filter(Boolean) as State
  flattened.unshift(DEFAULT_STATE[0])

  return flattened
}

export function* handleSendMessage(
  action: ReturnType<typeof actions.sendMessage>,
): Generator<object, void, unknown> {
  try {
    const state = (yield select(selectors.messages)) as VirtualCoachState['messages']

    yield put(actions.addUserMessage(action.payload))
    trackInternalEvent('chat_send_question', { question: action.payload })

    const { data } = (yield call(baseClient.post, API_ENDPOINT, {
      prompt: action.payload,
      chat_history: JSON.stringify(state),
      database_history: true,
    })) as ApiResponse<AiResponse>

    if (!data.success) throw new Error(data.body)

    const payload = {
      response: data.body.response,
      urlLinks: data.body.url_links,
    }

    yield put(actions.receiveMessage(payload))
    trackInternalEvent('chat_answer_received', { answer: payload.response })
  } catch (error) {
    yield put(actions.setError(ERROR_MESSAGE))
    trackInternalEvent('chat_error')
  }
}

export function* hydrateMessagesFromLocalStorage(): Generator<object, void, unknown> {
  try {
    yield put(actions.setLoading(true))
    const response = (yield call(baseClient.get, API_ENDPOINT_HISTORY)) as unknown as ApiResponse
    yield put(actions.setLoading(false))

    const { data: historyData } = response
    if (!historyData.success) throw new Error(historyData.body)

    const historyResponse = historyData.body

    if (historyResponse.length > 0) {
      const orderedHistory = reverseByPairs(historyResponse)
      yield put(actions.hydrateMessages(orderedHistory))
    } else {
      const userData = (yield select(userSelectors.userData)) as UserData | undefined
      if (!userData?.id) return

      const key = `${CLIENT_STORAGE_KEY}_${userData.id}`
      const stored = localStorage.getItem(key)

      if (!stored) {
        yield put(actions.hydrateMessages(DEFAULT_STATE))
        return
      }

      const parsed = JSON.parse(stored)
      if (!Array.isArray(parsed)) return

      yield put(actions.hydrateMessages(parsed))
    }
  } catch (e) {
    yield put(actions.setError(ERROR_MESSAGE))
    yield put(actions.setLoading(false))

    trackInternalEvent('chat_error')
  }
}

export function* sagas() {
  yield takeLatest(actions.openChatBot.type, hydrateMessagesFromLocalStorage)
  yield takeLatest(actions.sendMessage.type, handleSendMessage)
}
